import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../components/UserContext";
import axios from "axios";
import Cookies from "js-cookie";
import { BookingData } from "../../../utils/bookingUtils";
import { WorkspaceCardData } from "../../../components/Interfaces/WorkspaceCard";
import { SearchWorkspaces } from "../../../components/Search";
import HostWorkspaceCard from "../../../components/HostWorkspaceCard";

interface BookingResponse {
  success: boolean;
  message: string;
  bookings: BookingData[];
}

interface HostListingsPageProps {
  onManage?: () => void;
}

export default function HostListingsPage({ onManage }: HostListingsPageProps) {
  const { user } = useContext(UserContext);
  const [workspaces, setWorkspaces] = useState<WorkspaceCardData[]>([]);
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchAll = async () => {
      const token = Cookies.get("authToken");
      const [bookingsRes, workspacesRes] = await Promise.allSettled([
        axios.get<BookingResponse>(
          `/api/booking/list-workspace-owner?OwnerId=${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        SearchWorkspaces({
          location: "",
          moveInDate: null,
          capacity: null,
          userId: user.id,
          userOnly: true,
        }),
      ]);

      if (
        bookingsRes.status === "fulfilled" &&
        bookingsRes.value.data.success
      ) {
        setBookings(bookingsRes.value.data.bookings);
      }
      if (
        workspacesRes.status === "fulfilled" &&
        !(workspacesRes.value instanceof Error)
      ) {
        setWorkspaces(workspacesRes.value.workspaces);
      }
      setLoading(false);
    };

    fetchAll();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--secondary)] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading your listings…</span>
        </div>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="bg-white rounded-xl border border-gray-200 p-12 max-w-lg mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No workspaces yet
          </h3>
          <p className="text-gray-500 text-sm">
            Add your first workspace to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {workspaces.map((ws) => {
          const wsBookings = bookings.filter(
            (b) => b.workspaceId === ws.workspaceId
          );
          return (
            <HostWorkspaceCard
              key={ws.workspaceId}
              workspace={ws}
              bookings={wsBookings}
              onManage={onManage}
            />
          );
        })}
      </div>
    </div>
  );
}
